import { HttpCallbackArgWithSdTracking } from "./http-interceptor";
import { ignoreFuturePlsErrorsQueryStringParameter } from "../../ServiceDesk/sdServices/loading/prerequisite-loading-service";

export type RetryableConfig = {
  /** the limit to the number of retry attempts for a single request, the default is 3 retries */
  retryLimit?: number;

  /** the initial delay between retrying this request if there is a failure, the delay is increased by a factor of the number of failures, default to 250ms */
  retryDelayMilliseconds?: number;

  /** if from the given response the request should be retried, the default undefined which will not retry, see ServiceDesk.Services.RequestRetrying namespace for common functions */
  shouldRetry?: (response: { config: ng.IRequestConfig }) => boolean;
};

/** Retry on internal server (500+ status code) or internal browser failures (Seen as -1 status code) */
export const shouldRetryOnInternalServiceOrBrowserFailures = (response: ng.IHttpPromiseCallbackArg<{}>) =>
  response.status >= 500 || response.status == -1;

const defaultRetryDelayMilliseconds = 250;
const defaultRetryLimit = 3;

export type RetryableConfigInternal = RetryableConfig & {
  retryCount?: number;
};

export interface RequestRetryingService {
  /**
   * Retries a request so long as the configuration of the request deams it can be retried and hasn't hit the retry limit
   * Otherwise returns a rejected promise containing the response
   */
  retry(response: HttpCallbackArgWithSdTracking): ng.IPromise<any>;
}

export class RequestRetryingService implements RequestRetryingService {
  private $http: ng.IHttpService;

  // @ngInject
  constructor(
    private $injector: ng.auto.IInjectorService,
    private $timeout: ng.ITimeoutService,
    private $q: ng.IQService,
    private $window: ng.IWindowService
  ) {}

  initializeHttp() {
    if (!this.$http) {
      this.$http = this.$injector.get<ng.IHttpService>("$http");
    }
  }

  initializeConfig(config: RetryableConfigInternal) {
    if (!config) {
      return false;
    }

    if (typeof config.retryCount === "undefined") {
      config.retryCount = 0;
    }

    if (typeof config.retryLimit === "undefined") {
      config.retryLimit = defaultRetryLimit;
    }

    if (typeof config.retryDelayMilliseconds === "undefined") {
      config.retryDelayMilliseconds = defaultRetryDelayMilliseconds;
    }

    return true;
  }

  shouldRetry(response: HttpCallbackArgWithSdTracking): boolean {
    if (!this.initializeConfig(response.config)) {
      return false;
    }

    if (response.config.retryCount >= response.config.retryLimit) {
      return false;
    }

    if (this.$window.location.href.includes(ignoreFuturePlsErrorsQueryStringParameter + "=true")) {
      console.warn("Bypassing retry logic since ignore loading failures is enabled");
      return false;
    }

    if (!angular.isFunction(response.config.shouldRetry)) {
      return false;
    }

    return response.config.shouldRetry(response);
  }

  retry(response: HttpCallbackArgWithSdTracking): ng.IPromise<any> {
    if (!this.shouldRetry(response)) {
      return this.$q.reject(response);
    }

    this.initializeHttp();
    response.config.retryCount++;
    let retriedRequestDeferal = this.$q.defer();
    let httpRequestRetry = () =>
      this.$http(response.config)
        .then((result) => retriedRequestDeferal.resolve(result))
        .catch((result) => retriedRequestDeferal.reject(result));
    this.$timeout(httpRequestRetry, response.config.retryDelayMilliseconds * response.config.retryCount, false);
    return retriedRequestDeferal.promise;
  }
}
